import { Box, Theme, useTheme } from "@mui/material";
import React, { lazy, Suspense, ComponentType } from "react";

export const styles = (theme: Theme) => ({
  card: {
    textAlign: "center",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    height: "100%",
  },
  iconContainer: {
    padding: theme.spacing(3),
  },
  small: {
    height: theme.spacing(4),
    width: theme.spacing(4),
  },
  smaller: {
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  mini: {
    // 50% of 'smaller' (smaller is spacing(3) => 24px, mini is spacing(1.5) => 12px)
    height: theme.spacing(1.5),
    width: theme.spacing(1.5),
  },
  standard: {
    height: theme.spacing(8),
    width: theme.spacing(8),
  },
});

export type IconSize = "standard" | "small" | "smaller" | "mini";

function useCreateIconStyle(color: { color: string }, size: IconSize) {
  const theme = useTheme();
  const classes = styles(theme);

  const iconStyle = classes[size];

  return {
    ...iconStyle,
    ...color,
  };
}

// Module-level cache keyed by icon name.
// Calling lazy() inside the function body creates a new reference on every
// render, which causes Suspense to re-suspend indefinitely.  Caching here
// ensures the same LazyExoticComponent reference is reused across re-renders.
const lazyIconCache = new Map<string, React.LazyExoticComponent<ComponentType<any>>>();

function getLazyIcon(iconName: string): React.LazyExoticComponent<ComponentType<any>> {
  if (!lazyIconCache.has(iconName)) {
    lazyIconCache.set(
      iconName,
      lazy(() =>
        // lazy() requires a default export, so we wrap the dynamic import
        import(`@mui/icons-material`).then((module: any) => ({
          default: module[iconName] as ComponentType<any>,
        }))
      ) as React.LazyExoticComponent<ComponentType<any>>,
    );
  }
  return lazyIconCache.get(iconName)!;
}

// Exported for test teardown only — not part of the public API.
export const _resetIconCache = () => lazyIconCache.clear();

// React component that dynamically loads an icon from the MUI library given
// its name.
export function DynamicIcon({
  iconName,
  color,
  size = "standard",
}: {
  iconName: string;
  color: { color: string };
  size?: IconSize;
}) {
  const style = useCreateIconStyle(color, size);
  const Icon = getLazyIcon(iconName);
  return (
    <Suspense fallback={<Box sx={style} />}>
      <Icon sx={style} />
    </Suspense>
  );
}
